import { EventEmitter, Injectable, Output } from '@angular/core';
import { ICompanySummary } from '@common/models/ICompanySummary';
import { ApiService } from '@app/core/services/api.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { IPortfolioCompany } from "@common/models/IPortfolioCompany"
import { IReview } from '@common/models/IReview';
import { IAttachment } from '@common/models/IAttachment';
import { SystemPropertiesService } from '@app/core/services/system-properties.service';
import { IAttachmentType, ISystemProperty } from '@common/models/ISystemProperty';
import { IApiStatus } from '@common/models/IApiStatus';
import { HttpHeaders } from '@angular/common/http';
import { getQuarter } from '@common/services/DateHelper';
import { SystemPropertyKey } from '@common/enums/SystemPropertyKey';

@Injectable({
    providedIn: 'root'
})
export class PortfolioReviewService {
  private readonly sharedData: Subject<any> = new BehaviorSubject<any>({});
  sharedData$: Observable<any> = this.sharedData.asObservable();

  private readonly sharedQuarter: Subject<any> = new BehaviorSubject<any>({});
  sharedQuarter$: Observable<any> = this.sharedQuarter.asObservable();

  private readonly isNewClick = new BehaviorSubject<boolean>(false);
  isNewClick$ = this.isNewClick.asObservable();

  private readonly isCancelled = new BehaviorSubject<boolean>(false);
  isCancelled$ = this.isCancelled.asObservable(); 

  private reviewData: any = {};
  private readonly sharedReviewDataSubject: Subject<any> = new BehaviorSubject<any>({});
  sharedReviewData$: Observable<any> = this.sharedReviewDataSubject.asObservable();

  private readonly sharedActiveReviewTab: Subject<any> = new BehaviorSubject<any>('');
  sharedActiveReviewTab$: Observable<any> = this.sharedActiveReviewTab.asObservable();

  private readonly sharedReviewSubTab: Subject<any> = new BehaviorSubject<any>('');
  sharedReviewSubTab$: Observable<any> = this.sharedReviewSubTab.asObservable();

  @Output() reviewEditedFields = new EventEmitter<void>();
  private companySummary$: Observable<any>;

    constructor(private readonly apiService: ApiService, 
      private readonly systemPropertiesService: SystemPropertiesService) {
    }

    public getAll(): Observable<ICompanySummary[]> {
        if(!this.companySummary$){
          this.companySummary$ = this.getCompanySummary().pipe(
            shareReplay(1)
          )
        }

        return this.companySummary$;
    }

    private getCompanySummary(): Observable<ICompanySummary[]>{
      return this.apiService.get('CompanySummary');
    }

    public getCompany(mnemonic: string): Observable<IPortfolioCompany> {
        return this.apiService.get(`PortfolioCompany/${mnemonic}`)
    }
    public newQuarterlyReview(): IReview {    
        return {
          name: '2024-Q2',
          attachments: [],
          requestedDate: new Date(Date.now())
        }
    }

    public getReviewEditedFields():any{
      return this.reviewEditedFields;
    }

    public updateReviewEditedFields(data){
      this.reviewEditedFields.emit(data);
    }

    public async setAttachments(quarterlyReview: IReview):Promise<void> {
        // The system properties key for the attachment types of the given capital call type
        const key: string = SystemPropertyKey.reviewAttachmentTypes;
        quarterlyReview.attachments = [];
        return new Promise<void>((resolve, reject) => {
          this.systemPropertiesService.getByKey<IAttachmentType[]>(key).subscribe({ 
            next: ((attachmentTypes) => {
              if(attachmentTypes){
                const types = attachmentTypes;
                for (const element of types) {
                  const type = element;
                  quarterlyReview.attachments.push({
                    fileName: '',
                    attachmentType: type.name,
                    index: 0,
                    mimeType: '',
                    path: '',
                    typeDefinition: type
                  });
                }

                // Final attachment ordering based on sequence
                quarterlyReview.attachments = quarterlyReview.attachments.sort((a, b) => {
                  return ((a.typeDefinition?.sequence || 0) - (b.typeDefinition?.sequence || 0));
                });
              }   
              resolve();
            }),        
            error: ((err)  => reject(err))
          })
        })
    }
    public async setAttachmentTypes(quarterlyReview: IReview):Promise<void> {
        const key: string = SystemPropertyKey.reviewAttachmentTypes;
        return new Promise<void>((resolve, reject) => {
          this.systemPropertiesService.getByKey<IAttachmentType[]>(key).subscribe({ 
            next: ((attachmentTypes) => {
              const values = attachmentTypes;
              for (const attachment of quarterlyReview.attachments) {
                const type = values.find((t: IAttachmentType) => t.name === attachment.attachmentType);
                attachment.typeDefinition = type;
              }

              // Final attachment ordering based on sequence
              quarterlyReview.attachments = quarterlyReview.attachments.sort((a, b) => {
                return ((a.typeDefinition?.sequence || 0) - (b.typeDefinition?.sequence || 0));
              });
              resolve();
            }),        
            error: ((err)  => reject(err))
          })
        })
    }
    //will replcae mnemonic with IPortfolioCompany.mnemonic
    uploadAttachment(mnemonic: any, quarterlyReview: IReview, attachment: IAttachment, formData: FormData): Observable<IAttachment>{
        return this.apiService.post(`PortfolioCompany/${mnemonic}/Review/${quarterlyReview.name}/Attachment/${attachment.attachmentType}/${attachment.index || 0 }`, formData)
    }
    public deleteAttachment(mnemonic: string, name: string, attachment: IAttachment): Observable<IApiStatus> {
      return  this.apiService.delete(`PortfolioCompany/${mnemonic}/Review/${name}/Attachment/${attachment.attachmentType}/${attachment.index || 0}/${attachment.fileName}`)
    }
    setData(updatedData) {
      this.sharedData.next(updatedData);
    }
    public update(mnemonic: string, requestBody: IReview): Observable<IApiStatus> {
      return this.apiService.put(`PortfolioCompany/${mnemonic}/Review`, requestBody)
    }
    public save(mnemonic: string, requestBody: IReview): Observable<IApiStatus> {
      return this.apiService.post(`PortfolioCompany/${mnemonic}/Review`, requestBody)
    }
    public fileUploadPreview(mnemonic: any, review: IReview, attachment: IAttachment): Observable<Blob> {
      const url = `PortfolioCompany/${mnemonic}/Review/${review.name}/Attachment/Preview`;
      return this.apiService.post(url, attachment, {
        headers: new HttpHeaders().set('Accept', attachment.mimeType),
        responseType: 'blob'
      })
    }
    public getReviewByName(mnemonic: string, name: string): Observable<any>{
      const url = `PortfolioCompany/${mnemonic}/Review/${name}`;
      return this.apiService.get(url);
    }
    public getAllReviewNames(mnemonic: string): Observable<string[]> {
      return this.apiService.get(`PortfolioCompany/${mnemonic}/Review`);
    }
    setQuarter(quarter){
      this.sharedQuarter.next(quarter)
    }

    public getTearsheetHtml(mnemonic: string, fund: string, quarter: string): Observable<string> {
      const options = {
        headers: new HttpHeaders().set('Accept', 'text/html'),
        responseType: 'text'
      };

      return this.apiService.getV2(`PortfolioCompany/${mnemonic}/Tearsheet/download/html/${fund}/${quarter}`, options);
    }

    public getInvestmentSummaryHtml(mnemonic: string, fund: string, quarter: string): Observable<string> {
      const options = {
        headers: new HttpHeaders().set('Accept', 'text/html'),
        responseType: 'text'
      };

      return this.apiService.getV2(`PortfolioCompany/${mnemonic}/Review/${fund}/${quarter}/InvestmentSummary/html`, options);
    }

    public generateInvestmentSummaryHtml(mnemonic: string, quarter: string): Observable<void> {
      const url = `PortfolioCompany/${mnemonic}/Review/${quarter}/InvestmentSummary`;

      return this.apiService.post(url, {});
    }

    public getReviewPdf(mnemonic: string, fund: string, quarter: string, downloadAsAttachment: boolean = false): Observable<any> {
      const url = `PortfolioCompany/${mnemonic}/Review/${fund}/${quarter}/InvestmentSummary/pdf`;
      
      let headers = new HttpHeaders()
        .set('Accept', 'application/pdf');
      
      // Add a custom header to request attachment download if specified
      if (downloadAsAttachment) {
        headers = headers.set('X-Content-Disposition', 'attachment');
      }

      return this.apiService.post(url, {}, {
        headers: headers,
        responseType: 'blob' as 'json',
        observe: 'response',
      });
    }
    generateNextQuarter(date1){
      const today = new Date(date1);
      const quarter = Math.floor(today.getMonth() / 3); let nextQuarter = ''
          let startDate = new Date(today.getFullYear(), quarter * 3 + (3), 1);
          let endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);
          nextQuarter = getQuarter(endDate)
      return  nextQuarter
    }
    setNewClickStatus(clicked: boolean): void {
      this.isNewClick.next(clicked);
    }
    setCancelStatus(clicked: boolean) : void {
      this.isCancelled.next(clicked);
    }
    setReviewData(data): void{
      this.reviewData = {...this.reviewData, ...data};
      this.sharedReviewDataSubject.next(this.reviewData);
    }
    public delete(mnemonic: string, name: string): Observable<IApiStatus> {
      return this.apiService.delete(`PortfolioCompany/${mnemonic}/Review/${name}`)
    }
    public cloneReview(mnemonic, name: string, newName: string): Observable<IReview> {
      return this.apiService.post(`PortfolioCompany/${mnemonic}/Review/${name}/clone/${newName}`, {})
    }
    public bulkUpsert(requestBody: IReview[]): Observable<IApiStatus> {
      return this.apiService.post(`PortfolioCompany/All/Review/bulk`, requestBody)
    }
    setActiveReviewTab(selectedTab){
      this.sharedActiveReviewTab.next(selectedTab)
    }
    setReviewSubTab(selectedSubTab){
      this.sharedReviewSubTab.next(selectedSubTab)
    }
    public versionHistory(mnemonic: string, model, submodel, name): Observable<any> {
      return this.apiService.get(`VersionHistory/${mnemonic}/${model}/${submodel}/${name}?objectID=`)
    }

    public getExecutionQuarter(): Observable<ISystemProperty> {
      return this.apiService.get(`SystemProperty/${SystemPropertyKey.executionQuarter}`);
    }

    public getReviewQuarters(){
      let quarters:any[] = [];
      this.systemPropertiesService.getByKey<string[]>(SystemPropertyKey.portfolioReviewQuarters).subscribe((res) => {
        quarters = [...res]
      });
      return quarters;
    }

    public downloadTemplate(mnemonic: string, name: string): Observable<any> {
      return this.apiService.post(`PortfolioCompany/${mnemonic}/Review/${name}/Attachment/Template/Download/Forecast`, {})
    }

    public getAvailableQuarterlyReports(mnemonic: string): Observable<any>{
      const url = `PortfolioCompany/${mnemonic}/Review/quarterlyReports/available-quarters`;
      return this.apiService.get(url);
    }
}
